import { Capacitor } from '@capacitor/core';
import {
  TRACK_BATCH_MAX,
  TRACK_BUFFER_MAX,
  type ClientTrackEventName,
  type CountryCode,
  type TrackEventInput,
  type TrackProps,
} from '@capsico/contracts';
import { api, errorStatus } from './client';

/*
 * W19 app events. track() never throws and never blocks the screen: events go into a small
 * localStorage buffer (at most TRACK_BUFFER_MAX, oldest dropped first) and are sent in batches
 * of TRACK_BATCH_MAX to POST /v1/events when the app regains focus, comes back online, or
 * shortly after an event. The server de-duplicates on the random event id, so a resend after
 * a lost response is harmless. No personal data is sent: the device id travels in the usual
 * device header and is hashed on the server.
 */

const BUFFER_KEY = 'capsico:customer:events';
/** Set to 'off' to stop analytics on this device (there is no account-level consent pref yet). */
export const ANALYTICS_OPT_OUT_KEY = 'capsico:analytics';
const FLUSH_DELAY_MS = 3_000;

export interface TrackFields {
  country?: CountryCode | null;
  townId?: string | null;
  outletId?: string | null;
  valueMinor?: number | null;
  props?: TrackProps;
}

function platform(): TrackEventInput['platform'] {
  const value = Capacitor.getPlatform();
  return value === 'android' || value === 'ios' ? value : 'web';
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function analyticsEnabled(): boolean {
  return storage()?.getItem(ANALYTICS_OPT_OUT_KEY) !== 'off';
}

export function setAnalyticsEnabled(enabled: boolean): void {
  const store = storage();
  if (!store) return;
  if (enabled) store.removeItem(ANALYTICS_OPT_OUT_KEY);
  else {
    store.setItem(ANALYTICS_OPT_OUT_KEY, 'off');
    store.removeItem(BUFFER_KEY);
  }
}

function readBuffer(): TrackEventInput[] {
  const raw = storage()?.getItem(BUFFER_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TrackEventInput[]).slice(-TRACK_BUFFER_MAX) : [];
  } catch {
    return [];
  }
}

function writeBuffer(events: TrackEventInput[]): void {
  const store = storage();
  if (!store) return;
  try {
    if (events.length === 0) store.removeItem(BUFFER_KEY);
    else store.setItem(BUFFER_KEY, JSON.stringify(events.slice(-TRACK_BUFFER_MAX)));
  } catch {
    // Storage full or blocked: analytics is best-effort.
  }
}

let inFlight = false;
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    void flushEvents();
  }, FLUSH_DELAY_MS);
}

/** Record one event. Safe to call anywhere; does nothing when analytics is switched off. */
export function track(name: ClientTrackEventName, fields: TrackFields = {}): void {
  if (!analyticsEnabled()) return;
  const event: TrackEventInput = {
    id: crypto.randomUUID(),
    name,
    at: new Date().toISOString(),
    app: 'customer',
    platform: platform(),
    appVersion: __APP_VERSION__.slice(0, 20),
    country: fields.country ?? null,
    townId: fields.townId ?? null,
    outletId: fields.outletId ?? null,
    valueMinor: fields.valueMinor ?? null,
    ...(fields.props ? { props: fields.props } : {}),
  };
  writeBuffer([...readBuffer(), event]);
  scheduleFlush();
}

/** Send buffered events, one batch at a time. Drops a batch the server rejects (4xx); keeps it on network / 5xx. */
export async function flushEvents(): Promise<void> {
  if (inFlight || !analyticsEnabled()) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  inFlight = true;
  try {
    for (;;) {
      const buffer = readBuffer();
      if (buffer.length === 0) return;
      const batch = buffer.slice(0, TRACK_BATCH_MAX);
      try {
        await api.post('/v1/events', { events: batch });
      } catch (error) {
        const status = errorStatus(error);
        if (status === null || status >= 500 || status === 429) return;
      }
      const sent = new Set(batch.map((event) => event.id));
      writeBuffer(readBuffer().filter((event) => !sent.has(event.id)));
    }
  } finally {
    inFlight = false;
  }
}

let started = false;

/** Flush on focus, when the tab becomes visible and when the network returns. Call once at start-up. */
export function startEventFlushing(): () => void {
  if (started) return () => undefined;
  started = true;
  const flush = () => void flushEvents();
  const onVisible = () => {
    if (document.visibilityState === 'visible') flush();
  };
  window.addEventListener('focus', flush);
  window.addEventListener('online', flush);
  document.addEventListener('visibilitychange', onVisible);
  flush();
  return () => {
    started = false;
    window.removeEventListener('focus', flush);
    window.removeEventListener('online', flush);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
